// Admin user management. List, invite, edit role, suspend/reactivate,
// resend reset, delete.

import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Shield, ShieldCheck, MailCheck, MoreHorizontal, RefreshCw } from 'lucide-react';
import { usersApi } from '../lib/api';
import type { AdminUserView, Role } from '@shared/types';

export default function UsersPage() {
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      const { users } = await usersApi.list();
      setUsers(users);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:py-10 sm:px-6">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl text-slate-900">Users</h1>
            <p className="text-sm text-slate-500 mt-1">Invite and manage who can access the portal.</p>
          </div>
          <button onClick={() => setShowInvite(true)} className="btn-primary self-start sm:self-auto">
            <Plus size={14} /> Invite user
          </button>
        </header>

        {err && <p className="text-sm text-red-600 mb-4">{err}</p>}
        {lastInviteUrl && (
          <div className="bg-blue-50 border border-blue-100 rounded-md p-3 mb-4 text-sm">
            <div className="flex items-start gap-2">
              <MailCheck size={14} className="text-blue-700 mt-0.5" />
              <div className="flex-1">
                <p className="text-blue-900 font-medium">Invite sent</p>
                <p className="text-blue-700 text-xs mt-1">If the email doesn't arrive, share this link directly:</p>
                <p className="font-mono text-[11px] break-all text-blue-800 bg-white border border-blue-100 rounded px-2 py-1 mt-1">{lastInviteUrl}</p>
              </div>
              <button onClick={() => setLastInviteUrl(null)} className="text-xs text-blue-700 hover:underline">Dismiss</button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
            {users.map((u) => (
              <UserRow key={u.id} user={u} onChange={refresh} />
            ))}
          </ul>
        )}
      </div>

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onCreated={(url) => { setLastInviteUrl(url); setShowInvite(false); refresh(); }}
        />
      )}
    </div>
  );
}

function UserRow({ user, onChange }: { user: AdminUserView; onChange: () => Promise<void> | void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  async function run(action: string, fn: () => Promise<unknown>) {
    setBusy(action);
    try {
      await fn();
      await onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
      setMenuOpen(false);
    }
  }

  const initials = (user.display_name || user.email).slice(0, 2).toUpperCase();

  return (
    <li className="relative px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">{initials}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900 truncate">{user.display_name || user.email}</span>
          {user.role === 'admin' && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
              <ShieldCheck size={10} /> admin
            </span>
          )}
          {user.status !== 'active' && (
            <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${user.status === 'suspended' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{user.status}</span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">
          {user.email}
          {user.passkey_count > 0 && <> · {user.passkey_count} passkey{user.passkey_count === 1 ? '' : 's'}</>}
          {user.last_login_at && <> · last seen {new Date(user.last_login_at).toLocaleDateString()}</>}
        </div>
      </div>
      <button onClick={() => setMenuOpen((o) => !o)} className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600">
        <MoreHorizontal size={14} />
      </button>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-3 top-12 z-30 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-52">
            {user.role === 'admin' ? (
              <button onClick={() => run('demote', () => usersApi.update(user.id, { role: 'editor' }))} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2">
                <Shield size={12} /> Demote to editor
              </button>
            ) : (
              <button onClick={() => run('promote', () => usersApi.update(user.id, { role: 'admin' }))} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2">
                <ShieldCheck size={12} /> Promote to admin
              </button>
            )}
            {user.status === 'active' ? (
              <button onClick={() => run('suspend', () => usersApi.suspend(user.id))} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2">
                <Trash2 size={12} className="text-amber-600" /> Suspend
              </button>
            ) : user.status === 'suspended' ? (
              <button onClick={() => run('reactivate', () => usersApi.reactivate(user.id))} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2">
                <RefreshCw size={12} /> Reactivate
              </button>
            ) : null}
            {user.status !== 'suspended' && (
              <button onClick={() => run('reset', () => usersApi.passwordReset(user.id))} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2">
                <MailCheck size={12} /> Send password reset
              </button>
            )}
            <div className="border-t border-slate-100 my-1" />
            <button
              onClick={() => {
                if (confirm(`Permanently delete ${user.email}? This cannot be undone.`)) {
                  run('delete', () => usersApi.remove(user.id));
                }
              }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
            >
              <Trash2 size={12} /> Delete user
            </button>
          </div>
        </>
      )}
      {busy && <span className="text-xs text-slate-400 ml-2">{busy}…</span>}
    </li>
  );
}

function InviteModal({ onClose, onCreated }: { onClose: () => void; onCreated: (url: string) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const result = await usersApi.invite(email.trim(), name.trim() || undefined, role);
      onCreated(result.invite_url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <h2 className="font-display text-xl text-slate-900">Invite user</h2>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Email</label>
          <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Display name (optional)</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Role</label>
          <div className="flex gap-2">
            <RoleOption checked={role === 'editor'} onChange={() => setRole('editor')} label="Editor" hint="Can edit drafts" />
            <RoleOption checked={role === 'admin'} onChange={() => setRole('admin')} label="Admin" hint="Manages users too" />
          </div>
        </div>
        {err && <p className="text-xs text-red-600">{err}</p>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button type="submit" disabled={submitting} className="btn-primary flex-1 justify-center">
            {submitting ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>
    </div>
  );
}

function RoleOption({ checked, onChange, label, hint }: { checked: boolean; onChange: () => void; label: string; hint: string }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`flex-1 text-left p-3 rounded-md border transition-colors ${checked ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
    >
      <div className="text-sm font-medium text-slate-900">{label}</div>
      <div className="text-[11px] text-slate-500">{hint}</div>
    </button>
  );
}
